/**
 * SplitInformantFlow
 * ==================
 *
 * A 4-step modal flow for the "Add a second informant" (SPLIT) operation.
 * This is used when one informant is currently assigned to both the Adulthood
 * and Childhood questionnaires, and the user wants to split them so a new
 * person handles one of the two.
 *
 * Steps:
 *   0 – Select which life stage to assign to the new informant
 *   1 – Enter the new informant's details (name, email, relationship)
 *   2 – Review a before/after recap and confirm
 *   3 – Success confirmation
 *
 * The component is self-contained in terms of its internal step management
 * but delegates the actual backend call to the parent via props.  This keeps
 * the flow reusable across both the provider and patient modal wrappers.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { UserIcon } from '@heroicons/react/outline';
import StepIndicator from './StepIndicator';
import InformantFormInput from './InformantFormInput';
import BeforeAfterRecap from './BeforeAfterRecap';
import WarningPanel from './WarningPanel';
import ConfirmationScreen from './ConfirmationScreen';
import ButtonSpinner from './ButtonSpinner';
import {
  validateNewInformant,
  validateEmailNotDuplicate,
} from '../../utils/informantValidation';

const STEPS = ['Select', 'Details', 'Review', 'Done'];

/**
 * @param {Object}   props
 * @param {Object}   props.existingInformant – The informant who currently owns both stages
 * @param {Array}    props.allInformants     – All informants (for duplicate email check)
 * @param {Function} props.onSplit           – (existingInformantId, lifeStageForNew, newDetails) => Promise
 * @param {Function} props.onBack            – Called when user clicks "Back" on step 0
 * @param {boolean}  props.actionLoading     – Whether the backend call is in progress
 * @param {string}   props.actionError       – Error message from the backend
 * @param {string}   props.actionSuccess     – Success message from the backend
 * @param {Function} props.clearActionState  – Reset actionError/actionSuccess
 * @param {Function} props.getProgressWarning – (informantId) => warning string or null
 * @param {Function} props.onFinish          – Called when user clicks "Finish" on success screen
 * @param {Object}   props.terminology       – { singular, plural } for patient-facing copy
 * @param {Function} props.sanitizeNameInput  – Name input sanitizer
 * @param {Function} props.sanitizeEmailInput – Email input sanitizer
 */
export default function SplitInformantFlow({
  existingInformant,
  allInformants = [],
  onSplit,
  onBack,
  actionLoading = false,
  actionError = null,
  actionSuccess = null,
  clearActionState,
  getProgressWarning,
  onFinish,
  isFinishing = false,
  terminology = { singular: 'Informant', plural: 'Informants' },
  sanitizeNameInput,
  sanitizeEmailInput,
}) {
  const [step, setStep] = useState(0);
  const [selectedStage, setSelectedStage] = useState(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    relationship: '',
  });
  const [formErrors, setFormErrors] = useState({});

  // The remaining stage is the one the existing informant keeps.
  const remainingStage = selectedStage === 'Adulthood' ? 'Childhood' : 'Adulthood';
  const progressWarning = getProgressWarning?.(existingInformant?.id);

  const handleSelectStage = useCallback((stage) => {
    setSelectedStage(stage);
    setFormData({ firstName: '', lastName: '', email: '', relationship: '' });
    setFormErrors({});
    clearActionState?.();
    setStep(1);
  }, [clearActionState]);

  const handleFormChange = useCallback((field, value) => {
    let sanitized = value;
    if (field === 'firstName' || field === 'lastName' || field === 'relationship') {
      sanitized = sanitizeNameInput?.(value) ?? value;
    } else if (field === 'email') {
      sanitized = sanitizeEmailInput?.(value) ?? value;
    }
    setFormData(prev => ({ ...prev, [field]: sanitized }));
    if (formErrors[field]) {
      setFormErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }, [formErrors, sanitizeNameInput, sanitizeEmailInput]);

  const handleProceedToReview = useCallback(() => {
    const errors = validateNewInformant(formData, allInformants);
    // Also check for duplicate email against existing informants
    if (!errors.email) {
      const dupErr = validateEmailNotDuplicate(formData.email, allInformants);
      if (dupErr) errors.email = dupErr;
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    clearActionState?.();
    setStep(2);
  }, [formData, allInformants, clearActionState]);

  const handleConfirmSplit = useCallback(async () => {
    try {
      await onSplit(existingInformant.id, selectedStage, {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        relationship: formData.relationship.trim(),
      });
      if (mountedRef.current) setStep(3);
    } catch {
      // Error state is managed by the hook via actionError
    }
  }, [onSplit, existingInformant, selectedStage, formData]);

  const handleBackToStage = useCallback(() => {
    setSelectedStage(null);
    clearActionState?.();
    setStep(0);
  }, [clearActionState]);

  const handleBackToDetails = useCallback(() => {
    clearActionState?.();
    setStep(1);
  }, [clearActionState]);

  // ── Step 0: Select life stage ──
  if (step === 0) {
    return (
      <div className="space-y-4">
        <StepIndicator steps={STEPS} currentStep={0} />

        <div className="border-b border-gray-200 pb-3">
          <h3 className="text-sm font-semibold text-gray-900">
            Add a Second {terminology.singular}
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Choose which questionnaire to assign to a new person.{' '}
            <span className="font-medium">{existingInformant?.firstName} {existingInformant?.lastName}</span>{' '}
            will keep the other.
          </p>
        </div>

        {/* Current informant card */}
        <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center" aria-hidden="true">
              <UserIcon className="w-4 h-4 text-violet-600" />
            </div>
            <div>
              <span className="text-sm font-semibold text-gray-800">
                {existingInformant?.firstName} {existingInformant?.lastName}
              </span>
              <span className="block text-xs text-gray-500">
                Currently assigned to both questionnaires
              </span>
            </div>
          </div>
        </div>

        {progressWarning && (
          <WarningPanel variant="warning">
            {progressWarning}
          </WarningPanel>
        )}

        {/* Stage selection buttons */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">
            Which questionnaire should the new person answer?
          </p>
          {['Adulthood', 'Childhood'].map(stage => (
            <button
              key={stage}
              type="button"
              onClick={() => handleSelectStage(stage)}
              className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-md text-left hover:bg-violet-50 hover:border-violet-200 transition-colors"
            >
              <div>
                <span className="block text-sm font-semibold text-gray-800">
                  {stage} questionnaire
                </span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  {existingInformant?.firstName} will keep the{' '}
                  {stage === 'Adulthood' ? 'Childhood' : 'Adulthood'} questionnaire
                </span>
              </div>
              <span className="text-violet-600 text-sm font-semibold">Select →</span>
            </button>
          ))}
        </div>

        {/* Back button */}
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-gray-500 hover:text-gray-700 py-2"
        >
          ← Back to summary
        </button>
      </div>
    );
  }

  // ── Step 1: Enter new informant details ──
  if (step === 1) {
    return (
      <div className="space-y-4">
        <StepIndicator steps={STEPS} currentStep={1} />

        <div className="border-b border-gray-200 pb-3">
          <h3 className="text-sm font-semibold text-gray-900">
            New {terminology.singular} Details
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            This person will answer the <span className="font-medium">{selectedStage?.toLowerCase()}</span> questionnaire.
          </p>
        </div>

        {progressWarning && (
          <WarningPanel variant="warning">
            Any existing progress on the {selectedStage?.toLowerCase()} questionnaire
            will be archived when the new {terminology.singular.toLowerCase()} is assigned.
          </WarningPanel>
        )}

        <div className="space-y-3">
          <InformantFormInput
            label="First name"
            value={formData.firstName}
            onChange={(e) => handleFormChange('firstName', e.target.value)}
            error={formErrors.firstName}
            required
            autoComplete="given-name"
          />
          <InformantFormInput
            label="Last name"
            value={formData.lastName}
            onChange={(e) => handleFormChange('lastName', e.target.value)}
            error={formErrors.lastName}
            required
            autoComplete="family-name"
          />
          <InformantFormInput
            label="Email address"
            type="email"
            value={formData.email}
            onChange={(e) => handleFormChange('email', e.target.value)}
            error={formErrors.email}
            required
            autoComplete="email"
          />
          <InformantFormInput
            label="Relationship to patient"
            value={formData.relationship}
            onChange={(e) => handleFormChange('relationship', e.target.value)}
            error={formErrors.relationship}
            required
            placeholder="e.g. Parent, Spouse, Sibling"
          />
        </div>

        {actionError && (
          <WarningPanel variant="error">{actionError}</WarningPanel>
        )}

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={handleBackToStage}
            className="flex-1 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={handleProceedToReview}
            className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-violet-600 rounded-md hover:bg-violet-700 transition-colors"
          >
            Review Details →
          </button>
        </div>
      </div>
    );
  }

  // ── Step 2: Review and confirm ──
  if (step === 2) {
    const beforeData = [
      {
        id: existingInformant.id,
        name: `${existingInformant.firstName} ${existingInformant.lastName}`,
        lifeStages: ['Adulthood', 'Childhood'],
      },
    ];

    const afterData = [
      {
        id: existingInformant.id,
        name: `${existingInformant.firstName} ${existingInformant.lastName}`,
        lifeStages: [remainingStage],
      },
      {
        id: 'new',
        name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
        lifeStages: [selectedStage],
        isNew: true,
      },
    ];

    return (
      <div className="space-y-4">
        <StepIndicator steps={STEPS} currentStep={2} />

        <div className="border-b border-gray-200 pb-3">
          <h3 className="text-sm font-semibold text-gray-900">
            Review Changes
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Please review the changes below before confirming.
          </p>
        </div>

        <BeforeAfterRecap
          before={beforeData}
          after={afterData}
          operationLabel={`Add Second ${terminology.singular}`}
        />

        {/* New informant summary */}
        <div className="bg-violet-50 border border-violet-200 rounded-md p-3">
          <h4 className="text-xs font-semibold text-violet-600 uppercase tracking-wider mb-2">
            New {terminology.singular}
          </h4>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <dt className="text-gray-600">Name</dt>
            <dd className="font-medium text-gray-800">{formData.firstName.trim()} {formData.lastName.trim()}</dd>
            <dt className="text-gray-600">Email</dt>
            <dd className="font-medium text-gray-800 break-all">{formData.email.trim()}</dd>
            <dt className="text-gray-600">Relationship</dt>
            <dd className="font-medium text-gray-800">{formData.relationship.trim()}</dd>
            <dt className="text-gray-600">Assigned to</dt>
            <dd className="font-medium text-gray-800">{selectedStage} questionnaire</dd>
          </dl>
        </div>

        {progressWarning && (
          <WarningPanel variant="warning">
            <p className="font-semibold mb-1">Progress will be archived</p>
            <p>
              {existingInformant.firstName}'s existing progress on the{' '}
              {selectedStage?.toLowerCase()} questionnaire will be safely archived.
              The new {terminology.singular.toLowerCase()} will start fresh.
            </p>
          </WarningPanel>
        )}

        <WarningPanel variant="info">
          A welcome email will be sent to <span className="font-semibold break-all">{formData.email.trim()}</span> with
          instructions to set up their account.
        </WarningPanel>

        {actionError && (
          <WarningPanel variant="error">{actionError}</WarningPanel>
        )}

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={handleBackToDetails}
            disabled={actionLoading}
            className="flex-1 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={handleConfirmSplit}
            disabled={actionLoading}
            className={`flex-1 px-4 py-2 text-sm font-semibold text-white rounded-md transition-colors ${
              actionLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-violet-600 hover:bg-violet-700'
            }`}
          >
            {actionLoading ? (
              <span className="inline-flex items-center gap-2">
                <ButtonSpinner />
                Setting up…
              </span>
            ) : `Confirm & Add ${terminology.singular}`}
          </button>
        </div>
      </div>
    );
  }

  // ── Step 3: Success ──
  return (
    <div>
      <StepIndicator steps={STEPS} currentStep={3} />
      <ConfirmationScreen
        headline={`${terminology.singular} added successfully!`}
        detail={`${formData.firstName.trim()} ${formData.lastName.trim()} has been assigned the ${selectedStage?.toLowerCase()} questionnaire. A welcome email has been sent to ${formData.email.trim()}.`}
        onFinish={onFinish}
        isLoading={isFinishing}
      />
    </div>
  );
}
